import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  sortBy,
  filter,
  debounce,
} from 'lodash';
import SortableTree, { map as mapTree } from 'react-sortable-tree';
import 'react-sortable-tree/style.css';
import {
  Grid,
  Icon,
  IconButton,
  withStyles,
  withMobileDialog,
  Tooltip,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from '@material-ui/core';
import Search from '@material-ui/icons/Search';

import TreeTheme from './TagTreeTheme';
import TagEditor from '../TagEditor';
import styles from './styles';

export const DEBOUNCE_TIME = 500;

const handleNodeMove = ({
  node,
  nextParentNode,
}, onEditTag) => {
  let request = {};
  if (nextParentNode === null) {
    request = { customActions: { removeParentTag: true } };
  } else {
    request = { parentTagId: nextParentNode.id };
  }
  onEditTag(node.id, request);
};

export class TagTreeComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      search: '',
      debouncedSearch: '',
    };
  }

  componentDidMount() {
    this.debouncedSearch = debounce(() => {
      this.setState((prevState) => ({ debouncedSearch: prevState.search }));
    }, DEBOUNCE_TIME);
  }

  handleSearchChange = (e) => {
    this.setState({ search: e.target.value });
    this.debouncedSearch();
  }

  render() {
    const {
      onTagListUpdate,
      onEditTag,
      onArchiveTagClick,
      handleOpenAddTagDialog,
      tags,
      tagType,
      tagTypes,
      includeArchived,
      classes,
    } = this.props;

    const sortedTreeData = mapTree({
      treeData: sortBy(filter(tags.filter((tag) => tag.tagType.id === tagType.id), (tag) => includeArchived || !tag.archived), 'name'),
      getNodeKey: (row) => row.node.id,
      callback: (row) => {
        const updatedTag = row.node;
        if (updatedTag.tagType) {
          updatedTag.title = (
            <TagEditor
              onEditTag={onEditTag}
              onArchiveTagClick={onArchiveTagClick}
              tag={updatedTag}
              tagTypes={tagTypes}
            />
          );
        }
        updatedTag.expanded = updatedTag.expanded === undefined
          ? true
          : updatedTag.expanded;
        return updatedTag;
      },
      ignoreCollapsed: false,
    });
    return (
      <Grid container className={classes.displayFlex}>
        <Grid item xs={12} className={classNames(classes.gridItem, classes.displayFlex)} key="tag-tree">
          <div className={classes.header}>
            <div className={classes.flex}>
              <h4>Tags</h4>
            </div>
            <FormControl variant="outlined">
              <OutlinedInput
                startAdornment={(
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )}
                value={this.state.search}
                onChange={this.handleSearchChange}
                placeholder="Search"
                margin="dense"
                className={classes.textField}
              />
            </FormControl>
            <IconButton
              size="medium"
              id="add-root-tag-button"
              aria-label="add"
              onClick={() => handleOpenAddTagDialog(tagType.id, null)}
            >
              <Icon>add</Icon>
            </IconButton>
          </div>
          <div className={classes.treeContainer}>
            <SortableTree
              innerStyle={{
                flex: 1,
                flexDirection: 'column',
                display: 'flex',
                overflow: 'auto',
                padding: '1rem',
              }}
              className={classes.displayFlex}
              scaffoldBlockPxWidth={25}
              rowHeight={40}
              theme={TreeTheme}
              getNodeKey={({ node }) => node.id}
              treeData={sortedTreeData}
              onChange={(treeData) => {
                onTagListUpdate(tags.filter((tag) => tag.tagType.id !== tagType.id).concat(treeData));
              }}
              onMoveNode={(data) => { handleNodeMove(data, onEditTag); }}
              generateNodeProps={({ node }) => ({
                buttons: [(
                  <div>
                    <Tooltip title="Add Child Tag">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenAddTagDialog(tagType.id, node.id)}
                        aria-label="Add"
                      >
                        <Icon style={{ fontSize: 20 }}>add</Icon>
                      </IconButton>
                    </Tooltip>
                  </div>
                )],
              })}
              searchQuery={this.state.debouncedSearch}
              onlyExpandSearchedNodes={this.state.debouncedSearch.length > 0}
              searchMethod={
                  ({ node }) => this.state.debouncedSearch.length > 0
                    && node.name.toLowerCase().indexOf(this.state.search.toLowerCase()) > -1
                }
            />
          </div>
        </Grid>
      </Grid>
    );
  }
}

TagTreeComponent.propTypes = {
  onTagListUpdate: PropTypes.func,
  onEditTag: PropTypes.func,
  onArchiveTagClick: PropTypes.func,
  handleOpenAddTagDialog: PropTypes.func,
  tags: PropTypes.array,
  tagTypes: PropTypes.array,
  tagType: PropTypes.object,
  includeArchived: PropTypes.bool,
  classes: PropTypes.object,
};

TagTreeComponent.defaultProps = {
  onTagListUpdate: () => {},
  onEditTag: () => {},
  onArchiveTagClick: () => {},
  handleOpenAddTagDialog: () => {},
  tags: [],
  tagTypes: [],
  tagType: {},
  includeArchived: false,
  classes: {},
};

export default (withMobileDialog({ breakpoint: 'xs' })(withStyles(styles)(TagTreeComponent)));
